// SPDX-License-Identifier: AGPL-3.0-or-later

/**
 * Chromium performs history back/forward when a mouse "back" (button 3) or
 * "forward" (button 4) mouseup reaches the browser unconsumed. Binding those
 * buttons to a keybind (push-to-talk, mute, ...) therefore also navigated the
 * SPA. The guard consumes the mouseup so the page stays put.
 *
 * Desktop suppresses history navigation for those buttons unconditionally; a
 * chat shell never wants it. In a regular browser only buttons that are bound
 * to a keybind are suppressed so ordinary browser behaviour is preserved.
 */

export const MOUSE_BACK_BUTTON = 3;
export const MOUSE_FORWARD_BUTTON = 4;

export function isHistoryNavigationMouseButton(button: number): boolean {
	return button === MOUSE_BACK_BUTTON || button === MOUSE_FORWARD_BUTTON;
}

export function collectBoundMouseButtons(combos: Iterable<{mouseButton?: number | null}>): Set<number> {
	const buttons = new Set<number>();
	for (const combo of combos) {
		if (combo.mouseButton == null) continue;
		buttons.add(combo.mouseButton);
	}
	return buttons;
}

export interface MouseHistoryNavigationGuardOptions {
	isDesktop: boolean;
	boundMouseButtons: ReadonlySet<number>;
}

export function shouldSuppressMouseHistoryNavigation(
	button: number,
	{isDesktop, boundMouseButtons}: MouseHistoryNavigationGuardOptions,
): boolean {
	if (!isHistoryNavigationMouseButton(button)) return false;
	return isDesktop || boundMouseButtons.has(button);
}

export interface MouseHistoryNavigationGuardSource {
	isDesktop: () => boolean;
	getBoundMouseButtons: () => ReadonlySet<number>;
}

export function attachMouseHistoryNavigationGuard(
	source: MouseHistoryNavigationGuardSource,
	target: EventTarget = window,
): () => void {
	const onMouseUp = (event: Event): void => {
		const {button} = event as MouseEvent;
		if (typeof button !== 'number') return;
		const suppress = shouldSuppressMouseHistoryNavigation(button, {
			isDesktop: source.isDesktop(),
			boundMouseButtons: source.getBoundMouseButtons(),
		});
		if (!suppress) return;
		event.preventDefault();
	};
	target.addEventListener('mouseup', onMouseUp, true);
	return () => {
		target.removeEventListener('mouseup', onMouseUp, true);
	};
}
